import {useLazyQuery} from "@apollo/client";
import {Suspense, useState} from "react";
import {Helmet} from "react-helmet-async";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {redirectIfUnavailableFabric} from "@/lib/handlers/utils.tsx";
import {GetSLAReportFormRequirements, GQL_GET_SLA_REPORT_FORM_REQUIREMENTS} from "@/lib/queries/checks.ts";
import {SLAReportsActions} from "@/pages/reports/sla/SLAReportsActions.tsx";
import {SLAReportsList} from "@/pages/reports/sla/SLAReportsList.tsx";
import {SLAReportUpdateDrawer} from "@/pages/reports/sla/SLAReportUpdateDrawer.tsx";

export const SLAReports = () => {
  const {user} = useCurrentUserContext();
  const [loadData, {data, loading}] = useLazyQuery<GetSLAReportFormRequirements>(GQL_GET_SLA_REPORT_FORM_REQUIREMENTS);
  const [showNewReportDrawer, setShowNewReportDrawer] = useState(false);

  const openNewReportDrawer = async () => {
    if (!data && !loading) {
      await loadData();
    }
    setShowNewReportDrawer(true);
  };

  const canCreate = user?.has_write_access;

  return (
    <ContentContainer
      title="SLA Reports"
      actionsBar={canCreate && <SLAReportsActions onCreate={openNewReportDrawer} />}
    >
      <Helmet>
        <title>SLA Reports</title>
      </Helmet>
      {showNewReportDrawer && data && (
        <SLAReportUpdateDrawer
          title="Create SLA Report"
          onClose={() => setShowNewReportDrawer(false)}
          tags={data.tags}
          monitoringServiceTypes={data.monitoringServiceTypes}
          slaReportGlobalLogoUrl={data.slaReportGlobalLogo.logo_url}
        />
      )}
      <UpErrorBoundary onError={redirectIfUnavailableFabric("reports")}>
        <Suspense>
          <SLAReportsList onCreate={canCreate ? openNewReportDrawer : undefined} />
        </Suspense>
      </UpErrorBoundary>
    </ContentContainer>
  );
};
